import { MqttClient, QoS } from 'mqtt';
import mqtt from 'mqtt/dist/mqtt';
import { ChatTopic, TopicType } from 'consts/common.const';
import { BehaviorSubject } from 'rxjs';

export interface MqttConfig {
  MQTT_HOST: string;
  MQTT_PORT: number;
  MQTT_CLEAN: boolean;
  MQTT_CONNECT_TIMEOUT: number;
  MQTT_USERNAME: string;
  MQTT_PASSWORD: string;
  MQTT_RECONNECT_PERIOD: number;
  MQTT_QOS: number;
}

export interface NewSubMessage<Data> {
  type: TopicType;
  data: Data;
}

export interface MqttPayload {
  topic: ChatTopic | string;
  data;
}

export const mqttSubject = new BehaviorSubject<MqttPayload>(null);

export class MqttService {
  private static client: MqttClient | null = null;

  private static config: MqttConfig = {} as MqttConfig;

  private static isConnectStatus: '' | 'connecting' | 'connected' | 'error' = '';

  public static getClient = () => {
    return this.client;
  };

  public static getConnectStatus = () => {
    return this.isConnectStatus;
  };

  public static connect = (config: MqttConfig, handleMessage?: (payload: MqttPayload) => void) => {
    this.config = config;

    if (!this.client) {
      const clientId = `mqtt_${Math.random().toString(16).slice(3)}`;

      const opt = {
        clientId,
        clean: Boolean(config.MQTT_CLEAN) || false,
        connectTimeout: Number(config.MQTT_CONNECT_TIMEOUT) || 4000,
        username: config.MQTT_USERNAME,
        password: config.MQTT_PASSWORD,
        reconnectPeriod: Number(config.MQTT_RECONNECT_PERIOD) || 1000,
      };

      this.client = mqtt.connect(`${config.MQTT_HOST}:${config.MQTT_PORT}`, opt);
    }

    this.client &&
      this.client.on('connect', () => {
        console.log('[MQTT] >> StatusCode:: connected');
        this.isConnectStatus = 'connected';
      });

    this.client &&
      this.client.on('error', (err) => {
        console.log(`[MQTT] >> StatusCode:: error, Message:: ${err}`);
        this.isConnectStatus = 'error';
        this.client.end();
      });

    this.client &&
      this.client.on('reconnect', () => {
        console.log('[MQTT] >> StatusCode:: reconnect');
        this.isConnectStatus = 'connecting';
      });

    handleMessage && this.onListenMessage(handleMessage);

    return this.client;
  };

  /**
   * @deprecated
   * Using subscribeAsync
   */
  public static subscribe = (topic) => {
    if (this.client) {
      this.unsubscribe(topic);

      requestAnimationFrame(() => {
        this.client.subscribe(topic, { qos: (Number(this.config.MQTT_QOS) || 0) as QoS }, (error) => {
          if (error) {
            console.log(`[MQTT] >> Subscribe to topics error, Message:: ${error}`);

            return;
          }

          console.log(`Subscribe topic ${topic} done`);
        });
      });
    }
  };

  public static subscribeAsync = (topic): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (this.client) {
        this.client.unsubscribe(topic, () => {
          this.client.subscribe(topic, { qos: (Number(this.config.MQTT_QOS) || 0) as QoS }, (error) => {
            if (error) {
              console.log(`[MQTT] >> subscribeAsync to topics error, Message:: ${error}`);

              return reject(false);
            }

            console.log(`subscribeAsync topic ${topic} done`);
            resolve(true);
          });
        });
      } else {
        reject(false);
      }
    });
  };

  public static unsubscribe = (topic) => {
    this.client &&
      this.client.unsubscribe(topic, (error) => {
        if (error) {
          console.log(`[MQTT] >> Unsubscribe to topics error, Message:: ${error}`);

          return;
        }

        console.log(`Unsubscribe topic ${topic} done`);
      });
  };

  public static publish = (topic, payload) => {
    this.client &&
      this.client.publish(topic, payload, { qos: (Number(this.config.MQTT_QOS) || 0) as QoS }, (error) => {
        if (error) {
          console.log(`[MQTT] >> Publish error, Message:: ${error}`);
        }
      });
  };

  public static disconnect = () => {
    this.client && this.client.end();
  };

  public static onListenMessage = (handleMessage: (payload: MqttPayload) => void) => {
    this.client &&
      this.client.on('message', (topic, message) => {
        let msg = message.toString();

        try {
          msg = JSON.parse(msg);
          // eslint-disable-next-line no-empty
        } catch (e) {}
        const payloadData = { topic, data: msg };

        console.log(`[MQTT] >> has message, Message:: ${JSON.stringify(payloadData)}`);

        handleMessage && handleMessage(payloadData);
      });
  };

  public static setData = (data) => {
    mqttSubject.next(data);
  };

  public static getData = async (callback: (data) => void) => {
    mqttSubject.subscribe((data) => {
      !!data && callback(data);
    });
  };
}
